import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";

export default function PartnerEmbed() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/partner/me").then((r) => setMe(r.data)); }, []);
  if (!me) return <div className="p-10 text-slate-500">Loading…</div>;

  const publicUrl = `${window.location.origin}/book/${me.slug}`;
  const embed = `<iframe src="${publicUrl}" width="100%" height="620" frameborder="0" style="border:1px solid #e2e8f0"></iframe>`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };

  return (
    <div className="p-10 max-w-[1000px]">
      <PortalPageHeader eyebrow="Distribution" title="Booking link & widget" description="Share your dedicated DiTours booking link, or embed the widget on your website." />

      <div className="grid grid-cols-1 gap-6">
        <Section title="Direct link">
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-sm text-[#0a0f1e] bg-slate-50 p-3 border border-slate-200 truncate" data-testid="partner-public-url">{publicUrl}</code>
            <button onClick={() => copy(publicUrl)} className="text-slate-500 hover:text-[#f59e0b] p-2" data-testid="copy-public-url"><Copy className="w-4 h-4" /></button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#f59e0b] p-2"><ExternalLink className="w-4 h-4" /></a>
          </div>
        </Section>

        <Section title="Iframe embed (concierge desk)">
          <pre className="text-xs font-mono text-[#0a0f1e] bg-slate-50 p-4 border border-slate-200 overflow-x-auto whitespace-pre-wrap">{embed}</pre>
          <button onClick={() => copy(embed)} className="mt-3 inline-flex items-center gap-2 h-10 px-4 border border-slate-300 text-slate-700 text-xs uppercase tracking-wide hover:border-[#f59e0b] hover:text-[#f59e0b]" data-testid="copy-embed"><Copy className="w-3 h-3" /> Copy snippet</button>
        </Section>

        <Section title="API key (direct integration)">
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-xs text-emerald-700 bg-slate-50 p-3 border border-slate-200 truncate">{me.api_key}</code>
            <button onClick={() => copy(me.api_key)} className="text-slate-500 hover:text-[#f59e0b] p-2"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Use this key with the DiTours API to create bookings programmatically.</p>
        </Section>

        <Section title="Preview">
          <a href={publicUrl} target="_blank" rel="noreferrer" className="block h-64 border-2 border-dashed border-slate-300 flex items-center justify-center text-center hover:border-[#f59e0b] transition-colors">
            <div>
              <ExternalLink className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <div className="text-sm text-[#0a0f1e]">Open your booking page in a new tab</div>
              <div className="text-xs text-slate-500 mt-1">{publicUrl}</div>
            </div>
          </a>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 p-6 shadow-sm">
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-4">{title}</p>
      {children}
    </div>
  );
}
